<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Settings as SettingsIcon } from 'lucide-vue-next'
import Tabs from '@/components/ui/Tabs.vue'
import AuthTab from '@/components/settings/AuthTab.vue'
import GlobalOptionsTab from '@/components/settings/GlobalOptionsTab.vue'
import BTTab from '@/components/settings/BTTab.vue'
import KeysTab from '@/components/settings/KeysTab.vue'
import AppearanceTab from '@/components/settings/AppearanceTab.vue'
import DangerZoneTab from '@/components/settings/DangerZoneTab.vue'
import UpdateTab from '@/components/settings/UpdateTab.vue'
import LogsView from '@/views/LogsView.vue'

const { t } = useI18n()

type TabKey =
  | 'auth'
  | 'global'
  | 'bt'
  | 'keys'
  | 'logs'
  | 'appearance'
  | 'update'
  | 'danger'

const activeTab = ref<TabKey>('auth')

const tabs = computed(() => [
  { key: 'auth',       label: t('settings.tab.auth') },
  { key: 'global',     label: t('settings.tab.global') },
  { key: 'bt',         label: t('settings.tab.bt') },
  { key: 'keys',       label: t('settings.tab.keys') },
  { key: 'logs',       label: t('settings.tab.logs') },
  { key: 'appearance', label: t('settings.tab.appearance') },
  { key: 'update',     label: t('settings.tab.update') },
  { key: 'danger',     label: t('settings.tab.danger') },
])

const activeLabel = computed(() => {
  const found = tabs.value.find((tab) => tab.key === activeTab.value)
  return found?.label ?? ''
})
</script>

<template>
  <div class="space-y-6">
    <!-- Page header -->
    <header class="flex flex-col gap-1">
      <h1 class="text-lg font-semibold text-fg">{{ t('settings.title') }}</h1>
      <p class="text-sm text-fg-muted">{{ t('settings.subtitle') }}</p>
    </header>

    <!-- Tab bar — horizontal scroll on small screens, dropdown on narrow mobile -->
    <div class="md:hidden">
      <label class="sr-only" for="settings-tab-select">{{ t('settings.title') }}</label>
      <div class="relative">
        <SettingsIcon
          :size="14"
          class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
          aria-hidden="true"
        />
        <select
          id="settings-tab-select"
          v-model="activeTab"
          class="w-full appearance-none rounded-md border border-border bg-bg-surface py-2 pl-9 pr-9 text-sm text-fg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option v-for="tab in tabs" :key="tab.key" :value="tab.key">
            {{ tab.label }}
          </option>
        </select>
        <span
          class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-fg-subtle"
          aria-hidden="true"
        >▾</span>
      </div>
    </div>

    <div class="hidden overflow-x-auto md:block">
      <Tabs v-model="activeTab" :items="tabs" variant="line" size="md" />
    </div>

    <!-- Current-tab hint on mobile (a11y echo of the dropdown selection) -->
    <p v-if="activeLabel" class="text-xs text-fg-muted md:hidden">
      {{ activeLabel }}
    </p>

    <!-- Tab panels (v-show keeps state cached when switching) -->
    <section class="min-h-[400px]">
      <AuthTab v-show="activeTab === 'auth'" />
      <GlobalOptionsTab v-show="activeTab === 'global'" />
      <BTTab v-show="activeTab === 'bt'" />
      <KeysTab v-show="activeTab === 'keys'" />
      <LogsView v-show="activeTab === 'logs'" />
      <AppearanceTab v-show="activeTab === 'appearance'" />
      <UpdateTab v-show="activeTab === 'update'" />
      <DangerZoneTab v-show="activeTab === 'danger'" />
    </section>
  </div>
</template>